import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import ContractsList from './pages/ContractsList';
import DeveloperEmails from './pages/DeveloperEmails';
import DeveloperContracts from './pages/DeveloperContracts';
import DeveloperListings from './pages/DeveloperListings';
import DeveloperLoans from './pages/DeveloperLoans';
import DeveloperReferralAgreements from './pages/DeveloperReferralAgreements';
import DeveloperCompliance from './pages/DeveloperCompliance';
import DeveloperSplitAttachments from './pages/DeveloperSplitAttachments';
import DeveloperInspection from './pages/DeveloperInspection';
import DeveloperSignatureEval from './pages/DeveloperSignatureEval';
import DeveloperRepairs from './pages/DeveloperRepairs';
import DeveloperSentEmails from './pages/DeveloperSentEmails';
import DeveloperFees from './pages/DeveloperFees';
import DeveloperTasks from './pages/DeveloperTasks';
import DeveloperMilestones from './pages/DeveloperMilestones';
import ContractDetail from './pages/ContractDetail';
import ListingDetail from './pages/ListingDetail';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import './App.css';

import Gatekeeper from './components/Gatekeeper';
import AuthSessionGuard from './components/AuthSessionGuard';
import Waitlist from './pages/Waitlist';
import Signup from './pages/Signup';
import DeveloperUsers from './pages/DeveloperUsers';
import DeveloperUserAnalytics from './pages/DeveloperUserAnalytics';
import DeveloperRevenue from './pages/DeveloperRevenue';
import DeveloperDemos from './pages/DeveloperDemos';
import DeveloperLeads from './pages/DeveloperLeads';
import DeveloperKeyPeople from './pages/DeveloperKeyPeople';
import DeveloperWaitlist from './pages/DeveloperWaitlist';
import DeveloperPendingEmails from './pages/DeveloperPendingEmails';
import DeveloperSkySlope from './pages/DeveloperSkySlope';
import DeveloperSkySlopeChecklists from './pages/DeveloperSkySlopeChecklists';
import DeveloperSkySlopeDiscrepancies from './pages/DeveloperSkySlopeDiscrepancies';
import DeveloperBuyerAgreements from './pages/DeveloperBuyerAgreements';
import BuyerAgreementDetail from './pages/BuyerAgreementDetail';
import ReferralAgreementDetail from './pages/ReferralAgreementDetail';
import BuyerAgreementCreate from './pages/BuyerAgreementCreate';
import FormMagicLinkLanding from './pages/FormMagicLinkLanding';
import DealReplaceMagicLinkLanding from './pages/DealReplaceMagicLinkLanding';
import PaymentMethodMagicLinkLanding from './pages/PaymentMethodMagicLinkLanding';
import PaymentMethodReturnLanding from './pages/PaymentMethodReturnLanding';
import SigningComplete from './pages/SigningComplete';
import SigningInvite from './pages/SigningInvite';
import ReferralLandingPage from './pages/ReferralLandingPage';
import CancellationSurveyLanding, { SurveyHostInvalidPage } from './pages/CancellationSurveyLanding';
import DeveloperSurveys from './pages/DeveloperSurveys';
import { getAuth0RedirectUri } from './utils/apiUrls';
import { portalAuthScope, consumePendingSignupPath, resetReauthenticationState, markFreshAuthRedirect } from './utils/authSession';
import { pathWithStoredDealReplace, persistDealReplaceParams } from './utils/dealReplaceConfirm';

const auth0RedirectUri = getAuth0RedirectUri() || window.location.origin;

// Auth0 configuration
const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN || 'your-domain.auth0.com',
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || 'your-client-id',
  authorizationParams: {
    redirect_uri: auth0RedirectUri,
    ...(process.env.REACT_APP_AUTH0_AUDIENCE && { audience: process.env.REACT_APP_AUTH0_AUDIENCE }),
    scope: portalAuthScope()
  },
  cacheLocation: 'localstorage',
  useRefreshTokens: true
};

const Auth0ProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    resetReauthenticationState();
    markFreshAuthRedirect();
    navigate(pathWithStoredDealReplace(appState?.returnTo || window.location.pathname));
  };

  return (
    <Auth0Provider
      {...auth0Config}
      onRedirectCallback={onRedirectCallback}
    >
      <PendingSignupResume />
      <PersistDealReplaceParams />
      {children}
    </Auth0Provider>
  );
};

const PersistDealReplaceParams = () => {
  const location = useLocation();

  useEffect(() => {
    persistDealReplaceParams(location.search, location.pathname);
  }, [location.pathname, location.search]);

  return null;
};

const PendingSignupResume = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }
    // Auth0 logout returns to origin root only. Do not consume the stash while
    // the SPA clears local session on /signup before the logout redirect runs.
    if (location.pathname !== '/') {
      return;
    }
    const pending = consumePendingSignupPath();
    if (pending) {
      navigate(pending, { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  return null;
};

const EnvironmentBanner = () => {
  const env = process.env.REACT_APP_ENVIRONMENT_NAME || 'Local';
  if (env === 'Production') return null;

  return (
    <div style={{
      backgroundColor: '#FEF3C7',
      color: '#92400E',
      padding: '8px 16px',
      textAlign: 'center',
      fontWeight: 'bold',
      borderBottom: '1px solid #F59E0B',
      fontSize: '14px',
      zIndex: 9999,
      position: 'relative'
    }}>
      ⚠️ This is the {env} Environment
    </div>
  );
};

function isSurveyHostname() {
  return typeof window !== 'undefined' && window.location.hostname === 'survey.keyflo.ai';
}

function App() {
  if (isSurveyHostname()) {
    return (
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <div className="App">
          <Routes>
            <Route path="/s/:token" element={<CancellationSurveyLanding />} />
            <Route path="*" element={<SurveyHostInvalidPage />} />
          </Routes>
        </div>
      </Router>
    );
  }

  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <Auth0ProviderWithNavigate>
        <AuthSessionGuard>
          <div className="App">
          <EnvironmentBanner />
          <Routes>
            {/* Public/Landing Routes */}
            <Route path="/waitlist" element={
              <Gatekeeper>
                <Waitlist />
              </Gatekeeper>
            } />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forms/:token" element={<FormMagicLinkLanding />} />
            <Route path="/deal-replace/:token" element={<DealReplaceMagicLinkLanding />} />
            <Route path="/payment/return" element={<PaymentMethodReturnLanding />} />
            <Route path="/payment/:token" element={<PaymentMethodMagicLinkLanding />} />
            <Route path="/signing/complete" element={<SigningComplete />} />
            <Route path="/signing/invite/:token" element={<SigningInvite />} />
            <Route path="/referrals/:token" element={<ReferralLandingPage />} />
            <Route path="/s/:token" element={<CancellationSurveyLanding />} />

            {/* Protected Routes wrapped in Gatekeeper */}
            <Route path="/" element={
              <Gatekeeper>
                <ContractsList />
              </Gatekeeper>
            } />
            <Route path="/listing/:id" element={
              <Gatekeeper>
                <ListingDetail />
              </Gatekeeper>
            } />
            <Route path="/buyer-agreement/new" element={
              <Gatekeeper>
                <BuyerAgreementCreate />
              </Gatekeeper>
            } />
            <Route path="/buyer-agreement/:id" element={
              <Gatekeeper>
                <BuyerAgreementDetail />
              </Gatekeeper>
            } />
            <Route path="/referral-agreement/:id" element={
              <Gatekeeper>
                <ReferralAgreementDetail />
              </Gatekeeper>
            } />
            <Route path="/contract/:id" element={
              <Gatekeeper>
                <ContractDetail />
              </Gatekeeper>
            } />
            <Route path="/profile" element={
              <Gatekeeper>
                <Profile />
              </Gatekeeper>
            } />
            <Route path="/settings" element={
              <Gatekeeper>
                <Settings />
              </Gatekeeper>
            } />
            <Route path="/dev/emails" element={
              <Gatekeeper>
                <DeveloperEmails />
              </Gatekeeper>
            } />
            <Route path="/dev/contracts" element={
              <Gatekeeper>
                <DeveloperContracts />
              </Gatekeeper>
            } />
            <Route path="/dev/listings" element={
              <Gatekeeper>
                <DeveloperListings />
              </Gatekeeper>
            } />
            <Route path="/dev/buyer-agreements" element={
              <Gatekeeper>
                <DeveloperBuyerAgreements />
              </Gatekeeper>
            } />
            <Route path="/dev/sent_emails" element={
              <Gatekeeper>
                <DeveloperSentEmails />
              </Gatekeeper>
            } />
            <Route path="/dev/pending_emails" element={
              <Gatekeeper>
                <DeveloperPendingEmails />
              </Gatekeeper>
            } />
            <Route path="/dev/fees" element={
              <Gatekeeper>
                <DeveloperFees />
              </Gatekeeper>
            } />
            <Route path="/dev/tasks" element={
              <Gatekeeper>
                <DeveloperTasks />
              </Gatekeeper>
            } />
            <Route path="/dev/milestones" element={
              <Gatekeeper>
                <DeveloperMilestones />
              </Gatekeeper>
            } />
            <Route path="/dev/loans" element={
              <Gatekeeper>
                <DeveloperLoans />
              </Gatekeeper>
            } />
            <Route path="/dev/referral_agreements" element={
              <Gatekeeper>
                <DeveloperReferralAgreements />
              </Gatekeeper>
            } />
            <Route path="/dev/compliance" element={
              <Gatekeeper>
                <DeveloperCompliance />
              </Gatekeeper>
            } />
            <Route path="/dev/splitattachments" element={
              <Gatekeeper>
                <DeveloperSplitAttachments />
              </Gatekeeper>
            } />
            <Route path="/dev/inspection" element={
              <Gatekeeper>
                <DeveloperInspection />
              </Gatekeeper>
            } />
            <Route path="/dev/signature-eval" element={
              <Gatekeeper>
                <DeveloperSignatureEval />
              </Gatekeeper>
            } />
            <Route path="/dev/repairs" element={
              <Gatekeeper>
                <DeveloperRepairs />
              </Gatekeeper>
            } />
            <Route path="/dev/users" element={
              <Gatekeeper>
                <DeveloperUsers />
              </Gatekeeper>
            } />
            <Route path="/dev/user_analytics" element={
              <Gatekeeper>
                <DeveloperUserAnalytics />
              </Gatekeeper>
            } />
            <Route path="/dev/surveys" element={
              <Gatekeeper>
                <DeveloperSurveys />
              </Gatekeeper>
            } />
            <Route path="/dev/leads" element={
              <Gatekeeper>
                <DeveloperLeads />
              </Gatekeeper>
            } />
            <Route path="/dev/key_people" element={
              <Gatekeeper>
                <DeveloperKeyPeople />
              </Gatekeeper>
            } />
            <Route path="/dev/revenue" element={
              <Gatekeeper>
                <DeveloperRevenue />
              </Gatekeeper>
            } />
            <Route path="/dev/demos" element={
              <Gatekeeper>
                <DeveloperDemos />
              </Gatekeeper>
            } />
            <Route path="/dev/waitlist" element={
              <Gatekeeper>
                <DeveloperWaitlist />
              </Gatekeeper>
            } />
            <Route path="/dev/skyslope" element={
              <Gatekeeper>
                <DeveloperSkySlope />
              </Gatekeeper>
            } />
            <Route path="/dev/skyslope_checklist" element={
              <Gatekeeper>
                <DeveloperSkySlopeChecklists />
              </Gatekeeper>
            } />
            <Route path="/dev/skyslope_discrepancies" element={
              <Gatekeeper>
                <DeveloperSkySlopeDiscrepancies />
              </Gatekeeper>
            } />
          </Routes>
          <footer className="global-footer">
            <p>&copy; {new Date().getFullYear()} KeyFlo AI</p>
          </footer>
          </div>
        </AuthSessionGuard>
      </Auth0ProviderWithNavigate>
    </Router>
  );
}

export default App;
